import BlogFooter from '@/components/ArticleFooter';
import { PostThumbProps } from '@/components/_blog-updates/PostThumb';
import SidebarTableOfContents from '@/components/_blog-updates/SidebarTableOfContents';
import {
  CaseStudyStats,
} from '@/components/_rebrand/CaseStudyStats';
import { CaseStudyStatsIntegrationLogo, CaseStudyStatsKeyIntegrations } from '@/components/_rebrand/CaseStudyStats/CaseStudyStats.types';
import { HeroWithBreadcrumb } from '@/components/_rebrand/HeroWithBreadcrumb';
import VizyBlogArticle from '@/components/vizy/VizyBlogArticle';
import { extractTocItems } from '@/components/vizy/VizyBlogArticle/VizyBlogArticle.utils';
import { CUSTOMER_STORIES_HOME } from '@/constants/routes';
import {
  CustomerStoryPathsQuery,
  CustomerStoryQuery,
  CustomerStoryQueryVariables,
  Customers_Customer_Entry,
} from '@/graphql';
import CustomerStoryQueryDocument from '@/graphql/queries/CustomerStory.gql';
import CustomerStoryPathsQueryDocument from '@/graphql/queries/CustomerStoryPaths.gql';
import { getPageProps } from '@/lib/craft-page';
import { createLogger } from '@/lib/debug';
import { fetchRelatedLinks } from '@/lib/graphite/fetch-related-links';
import { mapGraphiteApiResponseToCustomerStoryCardProps } from '@/lib/graphite/utilities';
import CraftClient from '@/lib/graphql';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { marginX, mediaBreakpointDown, mediaBreakpointUp } from '@/styles/layout/utils';
import { remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import Head from 'next/head';
import { FC, PropsWithChildren, useMemo } from 'react';
import styled from 'styled-components';

// #region - Components

const Article = styled.article`
  max-width: ${remCalc(960)};
  ${marginX('auto')};
  width: 100%;

  ${mediaBreakpointDown('tablet')} {
    padding-bottom: ${remCalc(60)};
  }
`;


const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  margin-bottom: ${remCalc(60)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: minmax(${remCalc(280)}, ${remCalc(370)}) minmax(${remCalc(500)}, ${remCalc(700)});
    grid-template-rows: repeat(2, auto);
    column-gap: ${remCalc(40)};
    justify-content: space-between;
    row-gap: ${remCalc(40)};
    margin-top: ${remCalc(40)};

    ${Article} {
      grid-row: 2;
      grid-column: 2;
    }
  }
`;

const Sidebar = styled.aside`
  display: none;

  ${mediaBreakpointUp('desktop')} {
    display: flex;
    flex-direction: column;
    gap: ${remCalc(32)};
    grid-column: 1;
    grid-row: 2;
    position: sticky;
    top: ${remCalc(100)};
    align-self: start;
  }
`;

// #endregion

/**
 * Unfortunately, this is the type that matches craft interface
 */
type CustomerWithIntegrationLogos = {
  integrations?: Array<{
    title?: string | null;
    integrationLogo?: Array<{ url?: string | null } | null> | null;
    url?: string | null;
  } | null> | null;
} | null | undefined;

const getKeyIntegrations = (customer: CustomerWithIntegrationLogos): CaseStudyStatsKeyIntegrations => ({
  label: 'Key integrations',
  logos: (customer?.integrations ?? [])
    .map((integration) => {
      const logo = integration?.integrationLogo?.[0];

      if (!logo?.url) return null;

      const title = integration?.title?.trim() ?? '';
      const linkUrl = integration?.url?.trim() ?? '';

      return {
        src: logo.url,
        alt: title ? `${title} logo` : 'Integration logo',
        showTooltip: Boolean(title),
        label: title,
        href: linkUrl,
      } satisfies CaseStudyStatsIntegrationLogo;
    })
    .filter((logo): logo is CaseStudyStatsIntegrationLogo => logo !== null),
});

const mapCustomerData = (customer: Customers_Customer_Entry) => {

  const customerIndustry = customer?.industryCategories?.map((category) => category?.title).filter(Boolean).join(', ') ?? '—';

  const customerCompanySize = customer?.customerData?.find(
    (row) => (row?.heading ?? '').trim().toLowerCase() === 'company size',
  )?.value ?? '—';

  return {
    industry: {
      label: 'Industry',
      value: customerIndustry,
    },
    companySize: {
      label: 'Company size',
      value: customerCompanySize,
    },
  };
}

interface PageProps extends CustomerStoryQuery {
  relatedArticles?: PostThumbProps[];
}

const CustomerStory: PageTemplate<PageProps> = ({ entry, relatedArticles }) => {
  const customer = entry?.customer?.[0];
  const logoAsset = customer?.customerLogo?.[0];

  const { industry, companySize } = useMemo(() => mapCustomerData(customer as Customers_Customer_Entry), [customer]);

  const keyIntegrations = useMemo(() => getKeyIntegrations(customer), [customer?.integrations]);

  const tableOfContentsItems = useMemo(
    () => extractTocItems(entry?.blogArticleContent?.rawNodes),
    [entry?.blogArticleContent?.rawNodes],
  );

  const breadcrumbItems = useMemo(() => [
    { label: 'Customer stories', href: CUSTOMER_STORIES_HOME },
  ], []);


  return (
    <>
      {entry?.heroTitle && (
        <Head>
          <meta property="graphite:title" content={entry.heroTitle} />
        </Head>
      )}

      <HeroWithBreadcrumb
        className='pt-12 md:pt-18'
        breadcrumbItems={breadcrumbItems}
        companyName={customer?.title ?? ''}
        title={entry?.heroTitle ?? ''}
        image={logoAsset?.url ? {
          // TODO: replace with actual image from CMS
          src: entry?.coverImage?.[0]?.url!,
          alt: logoAsset.altText ?? customer?.title ?? '',
        } : { src: '', alt: '' }}
      />

      <CaseStudyStats
        industry={industry}
        companySize={companySize}
        keyIntegrations={keyIntegrations}
      />

      <section className="bg-white md:py-0 px-container-mobile md:px-container-desktop">
        <div className="mx-auto w-full max-w-[1460px]">
          <div className="rounded-s bg-gray-50 p-6 md:p-8 md:px-12">
            <ArticleGrid >
              <Article>
                <VizyBlogArticle nodes={entry?.blogArticleContent?.nodes} rawNodes={entry?.blogArticleContent?.rawNodes} />
              </Article>

              <Sidebar >
                {tableOfContentsItems.length > 0 && <SidebarTableOfContents title="In this article" items={tableOfContentsItems} />}
              </Sidebar>
            </ArticleGrid>
          </div>
        </div>
      </section>

      {relatedArticles && relatedArticles.length > 0 && (
        <div className='mt-3'>
          <BlogFooter carouselTitle="Related stories" theme="white" relatedArticles={relatedArticles} cropThumbImages={true} />

        </div>
      )}
    </>
  );
};

const CustomerStoryLayout: FC<PropsWithChildren> = (props) => (
  <MasterTemplate {...props} hideTopPattern={false} invert={true} className='md:-mt-[80px]' />
);

CustomerStory.template = CustomerStoryLayout;

export default CustomerStory;

export const getStaticPaths = getStaticPagePaths('customer-stories', async (context) => {
  const client = new CraftClient();

  const data = await client.request<CustomerStoryPathsQuery>(CustomerStoryPathsQueryDocument);

  const paths =
    data.entries?.map((path) => ({
      params: {
        slug: path?.slug!,
      },
    })) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});

export const getStaticProps = getStaticPageProps<PageProps, { slug: string }>(
  (params) => `customer-stories/${params?.slug}`,
  async ({ client, context: { params } }) => {
    const slug = params?.slug!;

    const logger = createLogger('pages/customer-stories/[slug].tsx');

    logger.info(`Building customer story: ${slug}`);

    try {
      const pageProps = await getPageProps<CustomerStoryQuery, CustomerStoryQueryVariables>({
        pathPrefix: '/customer-stories',
        queryDocument: CustomerStoryQueryDocument,
        client,
        variables: {
          slug,
        },
      });

      if (!pageProps) return { notFound: true };

      // Fetch related links for the customer story - 
      const graphiteRelatedLinks = await fetchRelatedLinks({
        pathPrefix: 'customer-stories',
        slug
      });

      const relatedArticles = mapGraphiteApiResponseToCustomerStoryCardProps(graphiteRelatedLinks);

      return {
        props: {
          ...pageProps,
          relatedArticles,
        },
      };
    } catch (error) {
      logger.error(`Error building customer story ${slug}:`, error);

      return { notFound: true };
    }
  },
);
